import React, { useCallback, useEffect, useState } from "react";
import AutenticacaoService from "../../services/AutenticacaoService";
import DownloadService from "../../services/DownloadService";
import RelatorioService from "../../services/RelatorioService";
import FlexSpinner from "../FlexFreteSpinner/FlexSpinner";
import Modal from "../FlexFreteModal/Modal";
import { GlobalFilterStyle } from "../MeusEnviosFreteList/style";
import {
  MultiSelectGlobalStyle,
  RelatorioEncomendasContainer,
  RelatorioFiltrosForm,
  TabelaWrapper,
} from "../RelatorioEncomendas/style";

const relatorioService = new RelatorioService();
const downloadService = new DownloadService();
const autenticacaoService = new AutenticacaoService();

const INITIAL_REQUEST = {
  size: 20,
  page: 0,
  sortDir: "desc",
  userUuid: "",
};

function formatCurrency(value) {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// Mapeamento de nome_produto → identificador visual da transportadora para agrupamento do cabeçalho.
// Produtos não listados aqui aparecem como coluna individual (sem agrupamento).
const PRODUTO_CARRIER = {
  ".Com": "Jadlog",
  Package: "Jadlog",
  Expres: "Jadlog",
  "Envio Standard": "J&T",
  PAC: "Correios",
  SEDEX: "Correios",
};

// Nome exibido na segunda linha do cabeçalho para cada produto.
// Se não listado, usa o nome original do produto.
const PRODUTO_DISPLAY = {
  "Envio Standard": "Standard",
};

function getCarrier(produto) {
  return PRODUTO_CARRIER[produto] || produto;
}

function getDisplayName(produto) {
  return PRODUTO_DISPLAY[produto] || produto;
}

function buildGrupos(items) {
  const gruposMap = new Map();
  items.forEach((item) => {
    item.cotacoes.forEach((c) => {
      const carrier = getCarrier(c.produto);
      if (!gruposMap.has(carrier)) gruposMap.set(carrier, new Map());
      if (!gruposMap.get(carrier).has(c.produto)) {
        gruposMap.get(carrier).set(c.produto, true);
      }
    });
  });

  return [...gruposMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([carrier, produtosMap]) => ({
      carrier,
      produtos: [...produtosMap.keys()].sort().map((produto) => ({ produto })),
    }));
}

function SortIcon({ sortDir }) {
  return <span className="sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;
}

export default function RelatorioCotacoes() {
  const usuario = autenticacaoService.usuarioCorrente();
  const isAdmin =
    usuario &&
    Array.isArray(usuario.roles) &&
    usuario.roles.some((r) => r === "ADMIN" || r === "ROLE_ADMIN");

  const [request, setRequest] = useState(INITIAL_REQUEST);
  const [filtroAtivo, setFiltroAtivo] = useState(INITIAL_REQUEST);
  const [cotacoes, setCotacoes] = useState([]);
  const [totalCotacoes, setTotalCotacoes] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportando, setIsExportando] = useState(false);
  const [erro, setErro] = useState({ isWrong: false, titulo: "", mensagem: "" });

  const carregarCotacoes = useCallback((filtro, append = false) => {
    setIsLoading(true);
    relatorioService.listarCotacoes(filtro, (err, data) => {
      setIsLoading(false);
      if (err) {
        setErro({
          isWrong: true,
          titulo: "Erro ao carregar relatório",
          mensagem: err.response?.data?.message || "Não foi possível carregar o relatório.",
        });
        return;
      }
      const items = data?.content ?? (Array.isArray(data) ? data : []);
      const total = data?.totalElements ?? items.length;
      setTotalCotacoes(total);
      setCotacoes((prev) => (append ? [...prev, ...items] : items));
    });
  }, []);

  useEffect(() => {
    carregarCotacoes(filtroAtivo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitForm = (e) => {
    e.preventDefault();
    const novoFiltro = { ...request, page: 0 };
    setFiltroAtivo(novoFiltro);
    setCotacoes([]);
    carregarCotacoes(novoFiltro);
  };

  const carregarMais = () => {
    const proximaPagina = filtroAtivo.page + 1;
    const novoFiltro = { ...filtroAtivo, page: proximaPagina };
    setFiltroAtivo(novoFiltro);
    carregarCotacoes(novoFiltro, true);
  };

  const handleSort = () => {
    const novaDir = filtroAtivo.sortDir === "asc" ? "desc" : "asc";
    const novoFiltro = { ...filtroAtivo, sortDir: novaDir, page: 0 };
    setFiltroAtivo(novoFiltro);
    setRequest((prev) => ({ ...prev, sortDir: novaDir }));
    setCotacoes([]);
    carregarCotacoes(novoFiltro);
  };

  const exportarCSV = () => {
    setIsExportando(true);
    const { userUuid, sortDir } = filtroAtivo;
    relatorioService.exportarCotacoesCSV({ userUuid, sortDir }, (err, data) => {
      setIsExportando(false);
      if (err) {
        setErro({
          isWrong: true,
          titulo: "Erro ao exportar",
          mensagem: "Não foi possível exportar o relatório.",
        });
      } else {
        downloadService.downloadFile({ label: "relatorio_cotacoes", data, mime: "text/csv" });
      }
    });
  };

  const handleSizeChange = (novoSize) => {
    const novoFiltro = { ...filtroAtivo, size: novoSize, page: 0 };
    setRequest((prev) => ({ ...prev, size: novoSize }));
    setFiltroAtivo(novoFiltro);
    setCotacoes([]);
    carregarCotacoes(novoFiltro);
  };

  const grupos = buildGrupos(cotacoes);
  const hasSubRows = grupos.some((g) => g.produtos.length > 1);
  const todasColunas = grupos.flatMap((g) => g.produtos.map((p) => p.produto));
  const temMais = cotacoes.length < totalCotacoes;

  return (
    <RelatorioEncomendasContainer>
      <MultiSelectGlobalStyle />
      <GlobalFilterStyle />

      {erro.isWrong && (
        <Modal
          visivel={erro.isWrong}
          titulo={erro.titulo}
          conteudo={erro.mensagem}
          autoCloseTime={8000}
          callbackModal={() => setErro({ isWrong: false, titulo: "", mensagem: "" })}
        />
      )}

      {isLoading && <FlexSpinner className="spinner" />}

      <RelatorioFiltrosForm>
        {isAdmin ? (
          <form onSubmit={submitForm}>
            <div className="formulario-titulo">
              <h2>Filtros</h2>
            </div>

            <div className="formulario-linha">
              <div className="formulario-busca-input-area">
                <label htmlFor="userUuid">UUID do usuário</label>
                <input
                  type="text"
                  id="userUuid"
                  className="input"
                  placeholder="Filtrar por usuário"
                  value={request.userUuid}
                  onChange={(e) => setRequest((prev) => ({ ...prev, userUuid: e.target.value }))}
                />
              </div>
            </div>

            <div className="formulario-acoes">
              <button className="button buscar-button" type="submit">
                Buscar
              </button>
              <button
                type="button"
                className="button exportar-button"
                onClick={exportarCSV}
                disabled={isExportando}
              >
                {isExportando ? "Exportando..." : "Exportar CSV"}
              </button>
            </div>
          </form>
        ) : (
          <div className="formulario-acoes">
            <button
              type="button"
              className="button exportar-button"
              onClick={exportarCSV}
              disabled={isExportando}
            >
              {isExportando ? "Exportando..." : "Exportar CSV"}
            </button>
          </div>
        )}
      </RelatorioFiltrosForm>

      <TabelaWrapper>
        <div className="tabela-cabecalho">
          <h2>
            Relatório de Cotações{" "}
            {totalCotacoes > 0 && (
              <span style={{ fontWeight: "normal", fontSize: "0.9rem", color: "#888" }}>
                ({cotacoes.length} de {totalCotacoes})
              </span>
            )}
          </h2>
          <div className="tabela-controles">
            <div className="select">
              <select
                value={filtroAtivo.size}
                onChange={(e) => handleSizeChange(Number(e.target.value))}
              >
                <option value="20">20 por página</option>
                <option value="50">50 por página</option>
                <option value="100">100 por página</option>
              </select>
            </div>
          </div>
        </div>

        {cotacoes.length === 0 && !isLoading ? (
          <div className="tabela-vazia">Nenhum registro encontrado.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="sortable" rowSpan={hasSubRows ? 2 : 1} onClick={handleSort}>
                  Pedido
                  <SortIcon sortDir={filtroAtivo.sortDir} />
                </th>
                {grupos.map((grupo) =>
                  grupo.produtos.length === 1 ? (
                    <th key={grupo.carrier} className="group-start" rowSpan={hasSubRows ? 2 : 1}>
                      {grupo.carrier}
                    </th>
                  ) : (
                    <th key={grupo.carrier} className="group-start" colSpan={grupo.produtos.length}>
                      {grupo.carrier}
                    </th>
                  )
                )}
                <th className="group-start" rowSpan={hasSubRows ? 2 : 1}>
                  Menor valor
                </th>
              </tr>
              {hasSubRows && (
                <tr>
                  {grupos.flatMap((grupo) =>
                    grupo.produtos.length <= 1
                      ? []
                      : grupo.produtos.map((p, pIdx) => (
                          <th key={p.produto} className={pIdx === 0 ? "group-start" : ""}>
                            {getDisplayName(p.produto)}
                          </th>
                        ))
                  )}
                </tr>
              )}
            </thead>
            <tbody>
              {cotacoes.map((item, idx) => (
                <tr key={item.id ?? idx}>
                  <td>{item.id || "-"}</td>
                  {todasColunas.map((produto) => {
                    const cot = item.cotacoes.find((c) => c.produto === produto);
                    return <td key={produto}>{cot ? formatCurrency(cot.valor) : "-"}</td>;
                  })}
                  <td>{formatCurrency(item.menorCotacao?.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {temMais && !isLoading && (
          <div className="tabela-rodape">
            <button className="button loadmore-button" type="button" onClick={carregarMais}>
              Carregar mais
            </button>
          </div>
        )}
      </TabelaWrapper>
    </RelatorioEncomendasContainer>
  );
}
